import React, { useEffect, useState } from "react";
import "./index.css"
import { Modal, Form, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchPermissions, updateRolePermissions } from "../../../redux/slice/adminRoleSlice";
import { useToast } from "../../../context/TostContext";
import CommonButton from "../../../components/commonButton";
import withModal from "../../../components/withModal";

const ManagePermissionModalInner = ({ onClose, role, onSuccess }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { permissions = [], isLoading } = useSelector((state) => state.adminRoles || {});
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    dispatch(fetchPermissions());
  }, [dispatch]);

  useEffect(() => {
    if (role && role.permissions) {
      const pIds = role.permissions.map((p) => p.id || p);
      setSelectedPermissions(pIds);
    } else {
      setSelectedPermissions([]);
    }
  }, [role]);

  const handleTogglePermission = (id) => {
    setSelectedPermissions((prev) =>
      prev.includes(id) ? prev.filter((pId) => pId !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    const res = await dispatch(updateRolePermissions({ id: role.id, permissionIds: selectedPermissions }));
    if (res.error) {
      toast.error(res.payload?.message || "Failed to update permissions");
    } else {
      toast.success("Permissions updated successfully");
      if (onSuccess) onSuccess();
      onClose();
    }
  };

  // Define the desired order for action columns
  const ACTION_ORDER = ["Create", "Read", "Update", "Delete", "Export", "Verify"];

  // Extract all unique actions (columns) and sort them by the predefined order
  const allActions = Array.from(
    new Set(
      permissions.flatMap((mod) => mod.permissions?.map((p) => p.action?.name) || [])
    )
  )
    .filter(Boolean)
    .sort((a, b) => {
      const indexA = ACTION_ORDER.indexOf(a);
      const indexB = ACTION_ORDER.indexOf(b);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });

  const allModules = permissions;

  const permissionMap = permissions.reduce((acc, mod) => {
    acc[mod.id] = (mod.permissions || []).reduce((pAcc, p) => {
      const actName = p.action?.name;
      if (actName) {
        pAcc[actName] = p.id;
      }
      return pAcc;
    }, {});
    return acc;
  }, {});

  return (<>
    <Modal.Body>
      <div className="permission-table">
        <Table bordered hover className=" mb-0">
          <thead className="sticky-top bg-light">
            <tr>
              <th className="bg-light text-uppercase small fw-bold text-muted" style={{ minWidth: "150px" }}>Modules</th>
              <th className="text-center bg-light text-uppercase small fw-bold text-muted">All</th>
              {allActions.map((action) => (
                <th key={action} className="text-center bg-light text-uppercase small fw-bold text-muted">
                  {action}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allModules.length === 0 ? (
              <tr>
                <td colSpan={allActions.length + 2} className="text-center py-4 text-muted">No permissions found</td>
              </tr>
            ) : (
              allModules.map((module) => {
                const modulePermissionIds = Object.values(permissionMap[module.id] || {});
                const isAllSelected = modulePermissionIds.length > 0 && modulePermissionIds.every(id => selectedPermissions.includes(id));

                const handleSelectAllModule = (checked) => {
                  if (checked) {
                    setSelectedPermissions(prev => Array.from(new Set([...prev, ...modulePermissionIds])));
                  } else {
                    setSelectedPermissions(prev => prev.filter(id => !modulePermissionIds.includes(id)));
                  }
                };

                return (
                  <tr key={module.id}>
                    <td className="fw-medium text-capitalize ps-3">{module.name}</td>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={(e) => handleSelectAllModule(e.target.checked)}
                        disabled={modulePermissionIds.length === 0}
                        className="d-inline-block cursor-pointer all-checkbox"
                      />
                    </td>
                    {allActions.map((actionName) => {
                      const permissionId = permissionMap[module.id]?.[actionName];
                      return (
                        <td key={actionName} className="text-center align-middle">
                          {permissionId ? (
                            <Form.Check
                              type="checkbox"
                              id={`perm-${permissionId}`}
                              checked={selectedPermissions.includes(permissionId)}
                              onChange={() => handleTogglePermission(permissionId)}
                              className="d-inline-block cursor-pointer"
                            />
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <CommonButton
        title={isLoading ? "Saving..." : "Save Permissions"}
        variant="primary"
        onClick={handleSubmit}
        disabled={isLoading}
      />
    </Modal.Footer>
  </>);
};

const ManagePermissionModal = withModal(ManagePermissionModalInner);

export default ManagePermissionModal;
